import { getHxIdFromGraphqlId } from './hxIdMap';
import { createCompositeId } from './compositeId';
import { HxConversationHeader, HxObjectID, HxViewType } from '@outlook/hxcore_web';
import { hxNotSupportedResult, HxFallbackResult } from 'owa-graph-hx-fallback-result';
import { ConversationSnapshot } from 'owa-graph-schema';

/**
 * Hx has a different HxConversationHeader for each HxView a conversation appears in, so we
 * use a composite id with unique disambiguation context to keep track of all the different HxConversationHeader's.
 * We need the context to be derivable from the folderId - since that's what we have in mutation resolvers.
 * But we also want the context to be derivable from the HxConversationHeader itself so that we can add it to the
 * mapping when we see a HxConversationHeader being loaded.
 *
 * The folderId is not available on HxConversationHeader, but viewHandle is (the unique part of the HxView objectId).
 * HxView/viewHandle is almost 1:1 with folderId. The one case where it's not is the inbox_other HxView. Luckily,
 * any particular conversation can only appear in the inbox HxView or the inbox_other HxView - never in both.
 * So for any particular conversation, there is only one HxConversationHeader we need to track with the inbox folderId.
 * To avoid confusion when looking for the HxConversationHeader with the inbox folderId, we always use the inbox viewHandle
 * even if the conversation was in inbox_other.
 *
 * When we add multi-account support we will likely need to track a map from accountId to inbox viewHandle.
 */

let inboxViewHandle: string | undefined;

export function setInboxViewHandle(viewHandle: number) {
    inboxViewHandle = viewHandle.toString();
}

export function getConversationHeaderIdMapContext(hxConversationHeader: HxConversationHeader) {
    return hxConversationHeader.parentViewType === HxViewType.Inbox_Other
        ? inboxViewHandle
        : hxConversationHeader.viewHandle.toString();
}

function tryGetHxConversationHeaderHxIdFromHxFolderId(
    conversationGraphqlId: string,
    folderHxId: HxObjectID
): HxObjectID | null {
    const compositeId = createCompositeId(conversationGraphqlId, folderHxId.getHandle().toString());
    return getHxIdFromGraphqlId(compositeId);
}

export function tryGetHxConversationHeaderIdFromMap(
    conversationGraphqlId: string,
    folderGraphqlId: string
): HxObjectID | null {
    const folderHxId = getHxIdFromGraphqlId(folderGraphqlId);
    if (!folderHxId) {
        return null;
    }

    return tryGetHxConversationHeaderHxIdFromHxFolderId(conversationGraphqlId, folderHxId);
}

export function tryGetHxConversationHeaderIdsFromMap(
    conversations: ConversationSnapshot[],
    folderGraphqlId: string,
    resolverName: string
): [HxObjectID, HxObjectID][] | HxFallbackResult {
    let hxObjectIds: [HxObjectID, HxObjectID][] = [];

    const folderHxId = getHxIdFromGraphqlId(folderGraphqlId);
    if (!folderHxId) {
        return hxNotSupportedResult(
            `${resolverName}: Cannot find hxId for folder: ${folderGraphqlId}`
        );
    }

    // forEach doesn't allow breaking or returning in the middle of a loop
    // So using for() instead.
    for (const conversation of conversations) {
        const objectId = tryGetHxConversationHeaderHxIdFromHxFolderId(conversation.id, folderHxId);

        if (!objectId) {
            return hxNotSupportedResult(
                `${resolverName}: Cannot find hxId for conversation: ${conversation.id} in folder: ${folderHxId}`
            );
        }

        hxObjectIds.push([objectId, HxObjectID.objectIdNil]);
    }

    return hxObjectIds;
}
